/*
 * File: CreateImageStory.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 20, 2025 at 11:54 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 3, 2025 at 12:01 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, forwardRef, useMemo, useCallback,
	useState
} from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';
import { useDropzone } from 'react-dropzone';

// Utilities
import { isValidNode, parseMarkdownLinks } from '../../../utilities/parser';
import { stripHtml } from '../../../../utilities/utilities';
import { toastMessage } from '../../../utilities/toaster';

// Components
import { Typography } from '../../Typography';
import { Button } from '../../Button';
import { LocaleLink } from '../../LocaleLink';
import { TextInput } from '../../TextInput';
import { TextArea } from '../../TextArea';
import { DragDrop } from '../../DragDrop';

// Constants
import { ACCEPTABLE_FILE_TYPES, AVAILABLE_I18N_NAMESPACES, FILE_UPLOAD_LIMITS } from '../../../../constants/base.constants';

// Styles
import * as S from './CreateImageStory.styles';


/**
 * Configuration
 */

// Parser
const ReactParser = new Parser();

// Node Definitions
const processNodeDefinitions = new ProcessNodeDefinitions();

// Parsing Instructions
const ParsingInstructions = [
	{
		shouldProcessNode(node) {
			return node.name === 'a';
		},
		processNode(node, children) {
			return <LocaleLink key={node.attribs.href} to={node.attribs.href} target="_blank">{children}</LocaleLink>;
		}
	},
	{
		shouldProcessNode() { return true; },
		processNode: processNodeDefinitions.processDefaultNode
	}
];


/**
 * Component
 */

export const CreateImageStory = forwardRef(({
	className,
	moveForward,
	moveBackward,
	artworkFile,
	setArtworkFile,
	artworkArtist,
	setArtworkArtist,
	artworkCaption,
	setArtworkCaption,
	context,
	isVisible
}, ref) => {

	// Create state handlers
	const [showUploader, setShowUploader] = useState(isVisible);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get partner content
	const partnerContent = useMemo(() => partner?.content?.pages?.share_story?.components?.create_image_story, [partner]);
	const warningContent = useMemo(() => partner?.content?.pages?.share_story?.components?.warnings, [partner]);

	// Get legal urls
	const reviewGuidelinesUrl = useMemo(() => (partner?.legal?.links?.review_guidelines ? partner?.legal?.links?.review_guidelines : `https://www.ourwave.org${locale.localePath}/review-guidelines`), [partner, locale]);
	const hasInternalLegalPages = useMemo(() => partner?.legal?.has_internal_legal_pages, [partner]);

	// Create file preview url
	const previewUrl = useMemo(() => {
		if (artworkFile) {
			return URL.createObjectURL(artworkFile);
		}
		return null;
	}, [artworkFile]);

	// Create dropzone callback
	const onDrop = useCallback(async (acceptedFiles) => {
		for (let i = 0; i < acceptedFiles.length; i += 1) {
			const file = acceptedFiles[i];

			// Set uploaded file
			setArtworkFile(file);
		}
	}, []);

	// Create accepted file types
	const accept = useMemo(() => {
		const a = {};
		ACCEPTABLE_FILE_TYPES.SURVIVOR_MEDIA.MIME_TYPES.forEach((type) => {
			a[type] = [];
		});
		return a;
	}, []);

	// Create dropzone from hook
	const dropzone = useDropzone({
		multiple: false,
		maxFiles: 1,
		minSize: FILE_UPLOAD_LIMITS.SURVIVOR_MEDIA.min,
		maxSize: FILE_UPLOAD_LIMITS.SURVIVOR_MEDIA.max,
		accept,
		onDrop
	});

	// Handle supported file types render
	const renderSupportedTypes = () => {
		const fileTypes = ACCEPTABLE_FILE_TYPES.SURVIVOR_MEDIA.EXTENSIONS.map((extension) => extension.toUpperCase()).join(', ');
		return t('pageShareStory.createImageStory.supportedFiles', { partnerName: partner.name, fileTypes });
	};

	// Handle next action
	const nextAction = () => {

		// Validate parameters
		if (artworkFile == null) {
			toastMessage(uiMode, partner, t('pageShareStory.createImageStory.errorMessage'));
			return;
		}

		// Move forward
		moveForward();
	};

	// Perform actions on visibility change
	useEffect(() => {
		if (isVisible) {

			// Update show uploader
			setShowUploader(true);

		} else {

			// Update show uploader
			setTimeout(() => {
				setShowUploader(false);
			}, 300);

		}
	}, [isVisible]);

	// Perform actions on component load
	useEffect(() => {}, []);

	// Return component
	return (
		<S.StepContainer ref={ref} className={className}>
			<S.InnerStep>

				{/* Content Container */}
				<S.ContentContainer>

					{/* Title */}
					<Typography tag="h1" weight="black">
						{!partnerContent?.title ? (
							<Trans t={t} i18nKey="pageShareStory.createImageStory.title">
								You are not alone.
							</Trans>
						) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.title[locale.localeBaseId]), isValidNode, ParsingInstructions)}
					</Typography>

					{/* Subtitle */}
					{context === 'story' ? (
						<Typography tag="p">
							{!partnerContent?.subtitle ? (
								<Trans t={t} i18nKey="pageShareStory.createImageStory.subtitleStory">
									We believe stories are powerful. If you feel comfortable, share your artwork and be a part of
									{' '}
									{{ partnerName: partner.name }}
								</Trans>
							) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.subtitle[locale.localeBaseId]), isValidNode, ParsingInstructions)}
						</Typography>
					) : (
						<Typography tag="p">
							{!partnerContent?.subtitle ? (
								<Trans t={t} i18nKey="pageShareStory.createImageStory.subtitleUpdate">
									We believe stories are powerful. If you feel comfortable, share your artwork to create an update to your story.
								</Trans>
							) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.subtitle[locale.localeBaseId]), isValidNode, ParsingInstructions)}
						</Typography>
					)}

				</S.ContentContainer>

				{/* Inputs */}
				{showUploader && (
					<S.InputContainer>

						{/* Media Dropzone */}
						<DragDrop dropzone={dropzone}>
							<S.DragZoneInner>
								{artworkFile
									? (
										<S.FilePreview>
											<S.RemoveButton onClick={(e) => {
												e.preventDefault();
												e.stopPropagation();
												setArtworkFile(null);
											}}
											>
												<FontAwesomeIcon icon={['far', 'times']} />
											</S.RemoveButton>
											<S.FileContainer src={previewUrl} />
										</S.FilePreview>
									) : (
										<>
											<FontAwesomeIcon icon={['far', 'arrow-up-from-bracket']} />
											<Typography tag="h5" weight="bold">
												<Trans t={t} i18nKey="pageShareStory.createImageStory.instructionSelectFile">
													Select an image to upload
												</Trans>
											</Typography>
											<Typography tag="h5" weight="regular">
												<Trans t={t} i18nKey="pageShareStory.createImageStory.instructionDragDrop">
													or drag and drop a file here
												</Trans>
											</Typography>
											<Typography tag="p" variation="3" weight="light">{renderSupportedTypes()}</Typography>
										</>
									)}
							</S.DragZoneInner>
						</DragDrop>

						{/* Artwork Artist */}
						<TextInput
							placeholder={t('pageShareStory.createImageStory.placeholderArtist')}
							name="message"
							type="text"
							value={artworkArtist || ''}
							onChange={(event) => {
								const { value } = event.target;
								setArtworkArtist(stripHtml(value));
							}}
						/>

						{/* Artwork Caption */}
						<TextArea
							placeholder={t('pageShareStory.createImageStory.placeholderCaption')}
							name="caption"
							type="text"
							rows={6}
							value={artworkCaption || ''}
							onChange={(event) => {
								const { value } = event.target;
								setArtworkCaption(stripHtml(value));
							}}
							autoResize
						/>

					</S.InputContainer>
				)}

				{/* Warnings */}
				<S.Warnings>

					{/* Minor Warning */}
					<Typography tag="p" variation="3">
						<Trans t={t} i18nKey="pageShareStory.minorWarning">
							To protect our community, we are only able to post stories by survivors who are currently at least 18 years old. For more info, check out our
							{' '}
							<LocaleLink to={reviewGuidelinesUrl} target={!hasInternalLegalPages ? '_blank' : null}>Review Guidelines</LocaleLink>
							.
						</Trans>
					</Typography>

					{/* Partner Warnings */}
					{warningContent && warningContent.length > 0 && warningContent.map((warning) => (
						<Typography tag="p" variation="3" key={warning.id}>
							{warning[locale.localeBaseId]}
						</Typography>
					))}

				</S.Warnings>

				{/* Continue Button */}
				<S.ActionContainer>
					<Button size="large" variant="text" onClick={moveBackward}>
						<FontAwesomeIcon icon={['fas', 'arrow-left-long']} />
						<Typography>
							<Trans t={t} i18nKey="actions.back">
								Back
							</Trans>
						</Typography>
					</Button>
					<Button size="large" onClick={nextAction}>
						<Typography>
							<Trans t={t} i18nKey="actions.continue">
								Continue
							</Trans>
						</Typography>
						<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
					</Button>
				</S.ActionContainer>

				{/* Legal Snippet */}
				<S.LegalSnippet />

			</S.InnerStep>
		</S.StepContainer>
	);
});


/**
 * Configuration
 */

CreateImageStory.displayName = 'CreateImageStory';
CreateImageStory.propTypes = {
	className: PropTypes.string,
	moveForward: PropTypes.func,
	moveBackward: PropTypes.func,
	artworkFile: PropTypes.shape(),
	setArtworkFile: PropTypes.func,
	artworkArtist: PropTypes.string,
	setArtworkArtist: PropTypes.func,
	artworkCaption: PropTypes.string,
	setArtworkCaption: PropTypes.func,
	context: PropTypes.string,
	isVisible: PropTypes.bool
};
CreateImageStory.defaultProps = {
	className: null,
	moveForward: null,
	moveBackward: null,
	artworkFile: null,
	setArtworkFile: null,
	artworkArtist: null,
	setArtworkArtist: null,
	artworkCaption: null,
	setArtworkCaption: null,
	context: 'story',
	isVisible: false
};
